// NEW_FILE_JUSTIFIED: Dependants CRUD section — new entity with no existing equivalent component
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Users, Plus } from 'lucide-react';
import { DependantCard } from '@/components/insurance/DependantCard';
import { useDependantsSection } from '@/hooks/useDependantsSection';
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: convex _generated is the documented exception
import type {
  IDependant,
  IDependantFormData,
  IDependantsSectionProps,
  TDependantRelationship,
} from '@/@types/dependants';

const RELATIONSHIP_OPTIONS: { value: TDependantRelationship; label: string }[] = [
  { value: 'spouse', label: 'Spouse' },
  { value: 'child', label: 'Child' },
  { value: 'parent', label: 'Parent' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'other', label: 'Other' },
];

const EMPTY_FORM: IDependantFormData = {
  name: '',
  dateOfBirth: '',
  memberId: '',
  relationship: 'spouse',
};

function isRelationship(value: string): value is TDependantRelationship {
  return ['spouse', 'child', 'parent', 'sibling', 'other'].includes(value);
}

export function DependantsSection({
  parentType,
  employeeId,
  leadId,
  orgId,
}: IDependantsSectionProps) {
  const {
    dependants,
    isLoading,
    editingId,
    setEditingId,
    isAdding,
    setIsAdding,
    handleCreate,
    handleUpdate,
    handleRemove,
    handleUploadDocument,
    handleRemoveDocument,
  } = useDependantsSection({ parentType, employeeId, leadId, orgId });

  const [form, setForm] = useState<IDependantFormData>(EMPTY_FORM);
  const [confirmRemoveId, setConfirmRemoveId] = useState<Id<'dependants'> | null>(null);

  const isDialogOpen = isAdding || editingId !== null;
  const isEditing = editingId !== null;

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setIsAdding(true);
  };

  const openEdit = (dependant: IDependant) => {
    setForm({
      name: dependant.name,
      dateOfBirth: dependant.dateOfBirth ?? '',
      memberId: dependant.memberId ?? '',
      relationship: dependant.relationship,
    });
    setEditingId(dependant._id);
  };

  const closeDialog = () => {
    setIsAdding(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleRelationshipChange = (value: string) => {
    if (isRelationship(value)) {
      setForm(f => ({ ...f, relationship: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    if (editingId) {
      await handleUpdate(editingId, form);
    } else {
      await handleCreate(form);
    }
    closeDialog();
  };

  if (isLoading) {
    return (
      <Card className="border border-border">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="w-5 h-5 text-blue-600" />
            Dependants
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <Skeleton className="h-20 w-full rounded-lg" />
          <Skeleton className="h-20 w-full rounded-lg" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="w-5 h-5 text-blue-600" />
              Dependants
              {dependants.length > 0 && (
                <span className="text-xs font-normal text-muted-foreground">
                  ({dependants.length})
                </span>
              )}
            </CardTitle>
            <Button variant="outline" size="sm" onClick={openAdd}>
              <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
              Add Dependant
            </Button>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {dependants.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">
              No dependants added yet. Click &quot;Add Dependant&quot; to add one.
            </p>
          ) : (
            dependants.map(dependant => (
              <DependantCard
                key={dependant._id}
                dependant={dependant}
                onEdit={openEdit}
                onRemove={id => setConfirmRemoveId(id)}
                onUploadDocument={handleUploadDocument}
                onRemoveDocument={handleRemoveDocument}
              />
            ))
          )}
        </CardContent>
      </Card>

      <Dialog
        open={isDialogOpen}
        onOpenChange={open => {
          if (!open) closeDialog();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit Dependant' : 'Add Dependant'}</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={e => {
              void handleSubmit(e);
            }}
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="dep-name">Full Name *</Label>
              <Input
                id="dep-name"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Jane Doe"
                required
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="dep-relationship">Relationship *</Label>
              <Select value={form.relationship} onValueChange={handleRelationshipChange}>
                <SelectTrigger id="dep-relationship">
                  <SelectValue placeholder="Select relationship" />
                </SelectTrigger>
                <SelectContent>
                  {RELATIONSHIP_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="dep-dob">Date of Birth</Label>
              <Input
                id="dep-dob"
                type="date"
                value={form.dateOfBirth}
                onChange={e => setForm(f => ({ ...f, dateOfBirth: e.target.value }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="dep-member-id">Member ID</Label>
              <Input
                id="dep-member-id"
                value={form.memberId}
                onChange={e => setForm(f => ({ ...f, memberId: e.target.value }))}
                placeholder="Optional member ID"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={!form.name.trim()}>
                {isEditing ? 'Save Changes' : 'Add Dependant'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmRemoveId !== null}
        onOpenChange={open => {
          if (!open) setConfirmRemoveId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove dependant?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the dependant and all their documents. This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={() => {
                if (confirmRemoveId) {
                  void handleRemove(confirmRemoveId);
                  setConfirmRemoveId(null);
                }
              }}
            >
              Remove
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
